/**
 * Renders a LegalDocument: title, dates, generated table of contents, and the
 * numbered sections.
 *
 * Every legal page goes through this one component, so they cannot drift apart
 * in structure, and a change to how citations or anchors work happens once.
 */
import { useI18n } from '../i18n';
import { COMPANY } from '../lib/constants';
import { Seo } from './Seo';
import type { Block, LegalDocument } from '../legal/types';
import styles from './LegalLayout.module.css';

interface LegalLayoutProps {
  doc: LegalDocument;
  /**
   * Path without the locale prefix, e.g. '/privacy'.
   *
   * The <title> and meta description for it live in src/shared/seo.ts, which
   * the server reads too — passing them in here would mean the same sentence
   * existing twice and eventually differing.
   */
  path: string;
}

export function LegalLayout({ doc, path }: LegalLayoutProps) {
  const { t, locale } = useI18n();

  /*
   * timeZone: 'UTC' is not optional here. `new Date('2026-08-23')` is parsed as
   * UTC midnight, so formatting it in a timezone behind UTC — Mexico City is
   * UTC-6 — renders the previous day. A legal document showing the wrong
   * effective date is exactly the kind of small error that matters.
   */
  const formattedDate = new Date(doc.lastUpdated).toLocaleDateString(
    locale === 'es' ? 'es-MX' : 'en-GB',
    { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' },
  );

  return (
    <>
      <Seo path={path} />

      <div className="container">
        <div className={styles.wrap}>
          <nav className={styles.toc} aria-label={t.legal.contents}>
            <h2 className={styles.tocTitle}>{t.legal.contents}</h2>
            <ol className={styles.tocList}>
              {doc.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className={styles.tocLink}>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <article className={styles.doc}>
            <h1 className={styles.title}>{doc.title}</h1>
            <p className={styles.intro}>{doc.intro}</p>

            <div className={styles.dates}>
              <span>
                {t.legal.lastUpdated}: <time dateTime={doc.lastUpdated}>{formattedDate}</time>
              </span>
              <span>
                {t.legal.effectiveDate}: <time dateTime={doc.lastUpdated}>{formattedDate}</time>
              </span>
            </div>

            {/*
              Required because the two language versions are not identical in
              force. For people in Mexico the Spanish one governs: a consumer
              contract made there is only valid in Spanish (LFPC art. 85).
              Everywhere else the English one does.
            */}
            <p className={styles.bindingNotice}>{t.legal.bindingNotice}</p>

            {doc.sections.map((section, index) => (
              <section key={section.id} id={section.id} className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.sectionNumber}>{String(index + 1).padStart(2, '0')}</span>
                  {section.title}
                </h2>
                {section.blocks.map((block, blockIndex) => (
                  <BlockView key={blockIndex} block={block} />
                ))}
              </section>
            ))}

            <aside className={styles.questions}>
              <h2 className={styles.questionsTitle}>{t.legal.questionsTitle}</h2>
              <p className={styles.questionsBody}>{t.legal.questionsBody}</p>
              <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>
            </aside>
          </article>
        </div>
      </div>
    </>
  );
}

/** One content block. Kept separate so the switch stays readable. */
export function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'p':
      return <p className={styles.p}>{block.content}</p>;

    case 'h3':
      return <h3 className={styles.h3}>{block.content}</h3>;

    case 'ul':
      return (
        <ul className={styles.ul}>
          {block.items.map((item, index) => (
            <li key={index} className={styles.li}>
              {item}
            </li>
          ))}
        </ul>
      );

    case 'dl':
      return (
        <dl className={styles.dl}>
          {block.items.map((item, index) => (
            <div key={index}>
              <dt className={styles.dt}>{item.term}</dt>
              <dd className={styles.dd}>{item.description}</dd>
            </div>
          ))}
        </dl>
      );

    case 'table':
      return (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th key={index} className={styles.th} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className={styles.td}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'callout':
      return <div className={styles.callout}>{block.content}</div>;
  }
}
